import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { coursesApi, modulesApi, Module, Lesson } from "../lib";
import { showSuccess, showError } from "../lib/toast";
import {
  ArrowLeft,
  Plus,
  Save,
  Trash2,
  Edit,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  BookOpen,
  Video,
  FileText,
  Code,
  HelpCircle,
  CheckSquare,
  GripVertical,
  Eye,
  Loader,
  X,
  Layout,
} from "lucide-react";

interface ModuleFormData {
  title: string;
  description: string;
  isPublished: boolean;
}

interface ModuleWithQuiz {
  _id: string;
  title: string;
  description: string;
  order: number;
  isPublished: boolean;
  lessons: Lesson[];
  totalDuration: number;
  totalLessons: number;
  hasQuiz?: boolean;
  quiz?: any;
}

interface LessonFormData {
  title: string;
  type: "video" | "text" | "code" | "quiz" | "assignment" | "mixed";
  duration: number;
  isPreview: boolean;
  isPublished: boolean;
  videoUrl: string;
  content: string;
  bullets: string;
  topics: TopicFormData[];
}

interface TopicFormData {
  title: string;
  description: string;
  duration: number;
  paragraphs: ParagraphFormData[];
}

interface ParagraphFormData {
  content: string;
}

const defaultLessonForm: LessonFormData = {
  title: "",
  type: "video",
  duration: 0,
  isPreview: false,
  isPublished: false,
  videoUrl: "",
  content: "",
  bullets: "",
  topics: [],
};

const defaultTopic: TopicFormData = {
  title: "",
  description: "",
  duration: 0,
  paragraphs: [],
};

const defaultParagraph: ParagraphFormData = {
  content: "",
};

export default function CourseModulesPage() {
  const navigate = useNavigate();
  const { id: courseId } = useParams<{ id: string }>();
  const [course, setCourse] = useState<any>(null);
  const [modules, setModules] = useState<ModuleWithQuiz[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Module state
  const [showAddModule, setShowAddModule] = useState(false);
  const [moduleForm, setModuleForm] = useState<ModuleFormData>({
    title: "",
    description: "",
    isPublished: false,
  });
  const [editingModule, setEditingModule] = useState<string | null>(null);

  // Lesson state
  const [expandedModule, setExpandedModule] = useState<string | null>(null);
  const [showAddLesson, setShowAddLesson] = useState<string | null>(null);
  const [lessonForm, setLessonForm] =
    useState<LessonFormData>(defaultLessonForm);
  const [editingLesson, setEditingLesson] = useState<string | null>(null);

  // Topic state
  const [showAddTopic, setShowAddTopic] = useState<string | null>(null);
  const [topicForm, setTopicForm] = useState<TopicFormData>(defaultTopic);
  const [editingTopic, setEditingTopic] = useState<string | null>(null);

  // Quiz state
  const [showAddQuiz, setShowAddQuiz] = useState<string | null>(null);
  const [quizForm, setQuizForm] = useState({
    title: "",
    description: "",
    timeLimit: 30,
    passingScore: 70,
    isCertificateExam: false,
    certificateTemplate: "default",
    examDuration: 120,
    minQuestions: 100,
  });
  const [editingQuiz, setEditingQuiz] = useState<string | null>(null);
  
  // Quiz Question state
  const [quizQuestions, setQuizQuestions] = useState<{
    questionText: string;
    type: "multiple-choice" | "true-false" | "short-answer";
    options: { text: string; isCorrect: boolean }[];
    points: number;
    explanation?: string;
  }[]>([]);
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [newQuestion, setNewQuestion] = useState({
    questionText: "",
    type: "multiple-choice" as "multiple-choice" | "true-false" | "short-answer",
    options: [{ text: "", isCorrect: false }, { text: "", isCorrect: false }],
    points: 1,
    explanation: "",
  });

  // Topic handlers
  const handleAddTopic = () => {
    if (!topicForm.title.trim()) {
      showError("Topic title is required");
      return;
    }
    const updated = {
      ...lessonForm,
      topics: [...lessonForm.topics, { ...topicForm }],
    };
    setLessonForm(updated);
    setTopicForm(defaultTopic);
    setShowAddTopic(null);
  };

  const handleUpdateTopic = (topicIndex: number) => {
    const updated = { ...lessonForm };
    updated.topics[topicIndex] = { ...topicForm };
    setLessonForm(updated);
    setEditingTopic(null);
    setTopicForm(defaultTopic);
  };

  const handleDeleteTopic = (topicIndex: number) => {
    const updated = { ...lessonForm };
    updated.topics = updated.topics.filter((_, i) => i !== topicIndex);
    setLessonForm(updated);
  };

  const startEditTopic = (topic: TopicFormData, index: number) => {
    setTopicForm(topic);
    setEditingTopic(`topic-${index}`);
    setShowAddTopic(null);
  };

  const addParagraphToTopic = (topicIndex: number) => {
    const updated = { ...lessonForm };
    updated.topics[topicIndex].paragraphs.push({ ...defaultParagraph });
    setLessonForm(updated);
  };

  const updateParagraphContent = (
    topicIndex: number,
    paraIndex: number,
    value: string,
  ) => {
    const updated = { ...lessonForm };
    updated.topics[topicIndex].paragraphs[paraIndex].content = value;
    setLessonForm(updated);
  };

  const removeParagraphFromTopic = (topicIndex: number, paraIndex: number) => {
    const updated = { ...lessonForm };
    updated.topics[topicIndex].paragraphs = updated.topics[
      topicIndex
    ].paragraphs.filter((_, i) => i !== paraIndex);
    setLessonForm(updated);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!courseId) return;

      try {
        setLoading(true);

        // Fetch course details
        const courseRes = await coursesApi.getCourseById(courseId);
        if (courseRes.success && courseRes.data) {
          setCourse(courseRes.data);
        }

        // Fetch modules
        const modulesRes = await modulesApi.getCourseModules(courseId);
        if (modulesRes.success) {
          setModules(modulesRes.data);
        }
      } catch (err) {
        showError("Failed to load course data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [courseId]);

  const handleAddModule = async () => {
    if (!courseId || !moduleForm.title.trim()) {
      showError("Module title is required");
      return;
    }

    setSaving(true);
    try {
      const response = await modulesApi.createModule(courseId, {
        title: moduleForm.title,
        description: moduleForm.description,
      });

      if (response.success) {
        setModules([...modules, response.data]);
        setModuleForm({ title: "", description: "", isPublished: false });
        setShowAddModule(false);
        showSuccess("Module created!");
      }
    } catch (err: any) {
      showError(err.message || "Failed to create module");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateModule = async (moduleId: string) => {
    if (!moduleForm.title.trim()) {
      showError("Module title is required");
      return;
    }

    setSaving(true);
    try {
      const response = await modulesApi.updateModule(moduleId, {
        title: moduleForm.title,
        description: moduleForm.description,
        isPublished: moduleForm.isPublished,
      });

      if (response.success) {
        setModules(
          modules.map((m) => (m._id === moduleId ? response.data : m)),
        );
        setEditingModule(null);
        setModuleForm({ title: "", description: "", isPublished: false });
        showSuccess("Module updated!");
      }
    } catch (err: any) {
      showError(err.message || "Failed to update module");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteModule = async (moduleId: string) => {
    if (!confirm("Delete this module and all its lessons?")) return;

    setSaving(true);
    try {
      await modulesApi.deleteModule(moduleId);
      setModules(modules.filter((m) => m._id !== moduleId));
      showSuccess("Module deleted");
    } catch (err: any) {
      showError(err.message || "Failed to delete module");
    } finally {
      setSaving(false);
    }
  };

  const handleAddLesson = async (moduleId: string) => {
    if (!lessonForm.title.trim()) {
      showError("Lesson title is required");
      return;
    }

    setSaving(true);
    try {
      const response = await modulesApi.createLesson(moduleId, {
        title: lessonForm.title,
        type: lessonForm.type,
        duration: lessonForm.duration,
        isPreview: lessonForm.isPreview,
        isPublished: lessonForm.isPublished,
        videoUrl: lessonForm.videoUrl,
        content: lessonForm.content,
        bullets: lessonForm.bullets,
        topics: lessonForm.topics,
      });

      if (response.success) {
        // Refresh modules to get updated lessons
        const modulesRes = await modulesApi.getCourseModules(courseId!);
        if (modulesRes.success) {
          setModules(modulesRes.data);
        }
        setLessonForm(defaultLessonForm);
        setShowAddLesson(null);
        showSuccess("Lesson created!");
      }
    } catch (err: any) {
      showError(err.message || "Failed to create lesson");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateLesson = async (lessonId: string) => {
    if (!lessonForm.title.trim()) {
      showError("Lesson title is required");
      return;
    }

    setSaving(true);
    try {
      const response = await modulesApi.updateLesson(lessonId, {
        title: lessonForm.title,
        type: lessonForm.type,
        duration: lessonForm.duration,
        isPreview: lessonForm.isPreview,
        isPublished: lessonForm.isPublished,
        videoUrl: lessonForm.videoUrl,
        content: lessonForm.content,
        bullets: lessonForm.bullets,
        topics: lessonForm.topics,
      });

      if (response.success) {
        // Refresh modules
        const modulesRes = await modulesApi.getCourseModules(courseId!);
        if (modulesRes.success) {
          setModules(modulesRes.data);
        }
        setEditingLesson(null);
        setLessonForm(defaultLessonForm);
        showSuccess("Lesson updated!");
      }
    } catch (err: any) {
      showError(err.message || "Failed to update lesson");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLesson = async (lessonId: string) => {
    if (!confirm("Delete this lesson?")) return;

    setSaving(true);
    try {
      await modulesApi.deleteLesson(lessonId);
      // Refresh modules
      const modulesRes = await modulesApi.getCourseModules(courseId!);
      if (modulesRes.success) {
        setModules(modulesRes.data);
      }
      showSuccess("Lesson deleted");
    } catch (err: any) {
      showError(err.message || "Failed to delete lesson");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateQuiz = async (moduleId: string) => {
    if (!quizForm.title.trim()) {
      showError("Quiz title is required");
      return;
    }

    setSaving(true);
    try {
      const response = await modulesApi.createQuiz(moduleId, {
        title: quizForm.title,
        description: quizForm.description,
        timeLimit: quizForm.timeLimit,
        passingScore: quizForm.passingScore,
        isCertificateExam: quizForm.isCertificateExam,
        certificateTemplate: quizForm.certificateTemplate,
        examDuration: quizForm.examDuration,
        minQuestions: quizForm.minQuestions,
      });

      if (response.success) {
        // Refresh modules
        const modulesRes = await modulesApi.getCourseModules(courseId!);
        if (modulesRes.success) {
          setModules(modulesRes.data);
        }
        setShowAddQuiz(null);
        setQuizForm({ title: "", description: "", timeLimit: 30, passingScore: 70, isCertificateExam: false, certificateTemplate: "default", examDuration: 120, minQuestions: 100 });
        showSuccess("Quiz created!");
      }
    } catch (err: any) {
      showError(err.message || "Failed to create quiz");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuiz = async (moduleId: string) => {
    if (!confirm("Delete this module's quiz? This action cannot be undone.")) return;

    setSaving(true);
    try {
      const response = await modulesApi.deleteQuiz(moduleId);

      if (response.success) {
        // Refresh modules
        const modulesRes = await modulesApi.getCourseModules(courseId!);
        if (modulesRes.success) {
          setModules(modulesRes.data);
        }
        showSuccess("Quiz deleted");
      }
    } catch (err: any) {
      showError(err.message || "Failed to delete quiz");
    } finally {
      setSaving(false);
    }
  };

  // Quiz question handlers
  const handleAddQuestion = () => {
    if (!newQuestion.questionText.trim()) {
      showError("Question text is required");
      return;
    }
    
    if (newQuestion.type === "multiple-choice") {
      const hasCorrect = newQuestion.options.some(o => o.isCorrect);
      if (!hasCorrect) {
        showError("Please select at least one correct answer");
        return;
      }
    }
    
    setQuizQuestions([...quizQuestions, { ...newQuestion }]);
    setNewQuestion({
      questionText: "",
      type: "multiple-choice",
      options: [{ text: "", isCorrect: false }, { text: "", isCorrect: false }],
      points: 1,
      explanation: "",
    });
    setShowAddQuestion(false);
  };

  const handleRemoveQuestion = (index: number) => {
    setQuizQuestions(quizQuestions.filter((_, i) => i !== index));
  };

  const handleUpdateOption = (optIndex: number, field: "text" | "isCorrect", value: string | boolean) => {
    const updatedOptions = [...newQuestion.options];
    if (field === "isCorrect" && value === true) {
      // If setting this as correct, uncheck others (single correct answer)
      updatedOptions.forEach((o, i) => updatedOptions[i] = { ...o, isCorrect: i === optIndex });
    } else {
      updatedOptions[optIndex] = { ...updatedOptions[optIndex], [field]: value };
    }
    setNewQuestion({ ...newQuestion, options: updatedOptions });
  };

  const handleAddOption = () => {
    setNewQuestion({ ...newQuestion, options: [...newQuestion.options, { text: "", isCorrect: false }] });
  };

  const handleRemoveOption = (optIndex: number) => {
    if (newQuestion.options.length <= 2) {
      showError("At least 2 options are required");
      return;
    }
    setNewQuestion({ ...newQuestion, options: newQuestion.options.filter((_, i) => i !== optIndex) });
  };

  const handleCreateQuizWithQuestions = async (moduleId: string) => {
    if (!quizForm.title.trim()) {
      showError("Quiz title is required");
      return;
    }

    setSaving(true);
    try {
      // First create the quiz
      const quizRes = await modulesApi.createQuiz(moduleId, {
        title: quizForm.title,
        description: quizForm.description,
        timeLimit: quizForm.timeLimit,
        passingScore: quizForm.passingScore,
      });

      if (quizRes.success && quizRes.data && quizQuestions.length > 0) {
        // Add questions to the quiz
        const quizId = quizRes.data._id;
        for (const question of quizQuestions) {
          // Transform to match backend format (uses underscore: multiple_choice)
          const qType = question.type === "true-false" ? "true_false" : "multiple_choice";
          const correctIdx = question.options?.findIndex((o: any) => o.isCorrect) ?? 0;
          const transformed = {
            questionText: question.questionText,
            type: qType,
            points: question.points || 1,
            explanation: question.explanation || "",
            options: question.options?.map((o: any, i: number) => ({
              id: `opt_${i}`,
              optionText: o.text,
            })) || [],
            correctOptionId: `opt_${correctIdx}`,
          };
          await modulesApi.addQuizQuestion(quizId, transformed);
        }
      }

      // Refresh modules
      const modulesRes = await modulesApi.getCourseModules(courseId!);
      if (modulesRes.success) {
        setModules(modulesRes.data);
      }
      
      setShowAddQuiz(null);
      setQuizForm({ title: "", description: "", timeLimit: 30, passingScore: 70, isCertificateExam: false, certificateTemplate: "default", examDuration: 120, minQuestions: 100 });
      setQuizQuestions([]);
      showSuccess(quizQuestions.length > 0 ? "Quiz created with questions!" : "Quiz created!");
    } catch (err: any) {
      showError(err.message || "Failed to create quiz");
    } finally {
      setSaving(false);
    }
  };

  const startEditModule = (module: ModuleWithQuiz) => {
    setModuleForm({
      title: module.title,
      description: module.description || "",
      isPublished: module.isPublished,
    });
    setEditingModule(module._id);
    setShowAddModule(false);
  };

  const startEditLesson = (lesson: Lesson) => {
    // Handle topics - might come as array or need parsing
    let topics: TopicFormData[] = [];
    if (lesson.topics && Array.isArray(lesson.topics)) {
      topics = lesson.topics.map((topic: any) => ({
        title: topic.title || "",
        description: topic.description || "",
        duration: topic.duration || 0,
        paragraphs:
          topic.paragraphs?.map((p: any) => ({
            content: p.content || "",
          })) || [],
      }));
    }

    setLessonForm({
      title: lesson.title,
      type: lesson.type,
      duration: lesson.duration,
      isPreview: lesson.isPreview,
      isPublished: lesson.isPublished,
      videoUrl: lesson.videoUrl || "",
      content: lesson.content || "",
      bullets: Array.isArray(lesson.bullets)
        ? lesson.bullets.join("\n")
        : lesson.bullets || "",
      topics,
    });
    setEditingLesson(lesson._id);
    setShowAddLesson(null);
  };

  const getLessonIcon = (type: string) => {
    switch (type) {
      case "video":
        return <Video className="w-4 h-4 text-blue-500" />;
      case "text":
        return <FileText className="w-4 h-4 text-amber-500" />;
      case "code":
        return <Code className="w-4 h-4 text-violet-500" />;
      case "mixed":
        return <Layout className="w-4 h-4 text-orange-500" />;
      case "quiz":
        return <HelpCircle className="w-4 h-4 text-rose-500" />;
      case "assignment":
        return <CheckSquare className="w-4 h-4 text-teal-500" />;
      default:
        return <BookOpen className="w-4 h-4 text-gray-400" />;
    }
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() =>
              navigate(`/dashboard/instructor/courses/${courseId}`)
            }
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Course Content</h1>
            <p className="text-sm text-gray-500 mt-1">{course?.title}</p>
          </div>
        </div>
        <button
          onClick={() => navigate(`/course/${courseId}`)}
          className="px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50 flex items-center gap-2"
        >
          <Eye className="w-4 h-4" />
          Preview
        </button>
      </div>

      {/* Add Module Button */}
      {!showAddModule && (
        <button
          onClick={() => setShowAddModule(true)}
          className="w-full py-3 border-2 border-dashed border-gray-200 rounded-lg text-gray-500 hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2 mb-6"
        >
          <Plus className="w-4 h-4" />
          Add Module
        </button>
      )}

      {/* Add Module Form */}
      {showAddModule && (
        <div className="bg-white rounded-lg border-2 border-gray-200 p-5 mb-6">
          <h3 className="text-base font-bold text-gray-900 mb-4">New Module</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Module Title *
              </label>
              <input
                type="text"
                value={moduleForm.title}
                onChange={(e) =>
                  setModuleForm({ ...moduleForm, title: e.target.value })
                }
                placeholder="e.g., Introduction to HTML"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Description
              </label>
              <textarea
                value={moduleForm.description}
                onChange={(e) =>
                  setModuleForm({ ...moduleForm, description: e.target.value })
                }
                placeholder="What will students learn in this module?"
                rows={3}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAddModule}
                disabled={saving}
                className="px-5 py-2.5 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Create Module
              </button>
              <button
                onClick={() => {
                  setShowAddModule(false);
                  setModuleForm({
                    title: "",
                    description: "",
                    isPublished: false,
                  });
                }}
                className="px-5 py-2.5 border border-gray-300 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Module Form */}
      {editingModule && (
        <div className="bg-white rounded-lg border-2 border-gray-200 p-5 mb-6">
          <h3 className="text-base font-bold text-gray-900 mb-4">
            Edit Module
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Module Title *
              </label>
              <input
                type="text"
                value={moduleForm.title}
                onChange={(e) =>
                  setModuleForm({ ...moduleForm, title: e.target.value })
                }
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Description
              </label>
              <textarea
                value={moduleForm.description}
                onChange={(e) =>
                  setModuleForm({ ...moduleForm, description: e.target.value })
                }
                rows={3}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
              />
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={moduleForm.isPublished}
                  onChange={(e) =>
                    setModuleForm({
                      ...moduleForm,
                      isPublished: e.target.checked,
                    })
                  }
                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                />
                Published
              </label>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleUpdateModule(editingModule)}
                disabled={saving}
                className="px-5 py-2.5 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Changes
              </button>
              <button
                onClick={() => {
                  setEditingModule(null);
                  setModuleForm({
                    title: "",
                    description: "",
                    isPublished: false,
                  });
                }}
                className="px-5 py-2.5 border border-gray-300 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modules List */}
      <div className="space-y-4">
        {modules.map((module, index) => (
          <div
            key={module._id}
            className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden"
          >
            {/* Module Header */}
            <div
              className="flex items-center gap-4 p-4 bg-gray-100 cursor-pointer hover:bg-gray-200"
              onClick={() =>
                setExpandedModule(
                  expandedModule === module._id ? null : module._id,
                )
              }
            >
              <div className="w-10 h-10 rounded-lg bg-primary text-white flex items-center justify-center text-base font-bold">
                {index + 1}
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900">
                  {module.title}
                </h3>
                <p className="text-sm text-gray-500">
                  {module.lessons?.length || 0} lessons ·{" "}
                  {formatDuration(module.totalDuration || 0)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    startEditModule(module);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteModule(module._id);
                  }}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                {expandedModule === module._id ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </div>
            </div>

            {/* Lessons */}
            {expandedModule === module._id && (
              <div className="border-t border-gray-100">
                {/* Add Lesson Button */}
                {showAddLesson !== module._id && !showAddQuiz && (
                  <div className="p-3 border-b border-gray-100 flex gap-2">
                    <button
                      onClick={() => setShowAddLesson(module._id)}
                      className="flex-1 text-sm text-gray-500 hover:text-primary hover:bg-gray-50 py-2 px-3 rounded-lg border border-dashed border-gray-200 hover:border-primary flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Lesson
                    </button>
                    <button
                      onClick={() => {
                        if (module.hasQuiz || module.quiz) {
                          showError("This module already has a quiz");
                          return;
                        }
                        setShowAddQuiz(module._id);
                      }}
                      className={`flex-1 text-sm py-2 px-3 rounded-lg border border-dashed flex items-center justify-center gap-2
                        ${module.hasQuiz || module.quiz 
                          ? "text-gray-300 border-gray-100 cursor-not-allowed" 
                          : "text-amber-600 border-amber-200 hover:bg-amber-50"}`}
                      >
                        <HelpCircle className="w-4 h-4" />
                        Add Quiz
                      </button>
                  </div>
                )}

                {/* Add Lesson Form */}
                {showAddLesson === module._id && (
                  <div className="p-5 border-b border-gray-100 bg-blue-50">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-base font-bold text-gray-900">
                        New Lesson
                      </h4>
                      <button
                        onClick={() => {
                          setShowAddLesson(null);
                          setLessonForm(defaultLessonForm);
                        }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Basic Info */}
                    <div className="mb-5 p-4 bg-white rounded-lg border border-gray-200">
                      <p className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">
                        Basic Info
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Title *
                          </label>
                          <input
                            type="text"
                            value={lessonForm.title}
                            onChange={(e) =>
                              setLessonForm({
                                ...lessonForm,
                                title: e.target.value,
                              })
                            }
                            placeholder="Lesson title"
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Type
                          </label>
                          <select
                            value={lessonForm.type}
                            onChange={(e) =>
                              setLessonForm({
                                ...lessonForm,
                                type: e.target.value as any,
                              })
                            }
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                          >
                            <option value="video">Video</option>
                            <option value="text">Text</option>
                            <option value="code">Code</option>
                            <option value="mixed">Mixed</option>
                            <option value="quiz">Quiz</option>
                            <option value="assignment">Assignment</option>
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Duration (min)
                          </label>
                          <input
                            type="number"
                            value={lessonForm.duration}
                            onChange={(e) =>
                              setLessonForm({
                                ...lessonForm,
                                duration: parseInt(e.target.value) || 0,
                              })
                            }
                            min="0"
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                          />
                        </div>
                        <div className="col-span-2 flex items-center gap-6 pt-6">
                          <label className="flex items-center gap-2 text-sm text-gray-600">
                            <input
                              type="checkbox"
                              checked={lessonForm.isPreview}
                              onChange={(e) =>
                                setLessonForm({
                                  ...lessonForm,
                                  isPreview: e.target.checked,
                                })
                              }
                              className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                            />
                            Free Preview
                          </label>
                          <label className="flex items-center gap-2 text-sm text-gray-600">
                            <input
                              type="checkbox"
                              checked={lessonForm.isPublished}
                              onChange={(e) =>
                                setLessonForm({
                                  ...lessonForm,
                                  isPublished: e.target.checked,
                                })
                              }
                              className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                            />
                            Published
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Video Content */}
                    {(lessonForm.type === "video" ||
                      lessonForm.type === "mixed") && (
                      <div className="mb-5 p-4 bg-blue-100 rounded-lg border border-blue-200">
                        <p className="text-sm font-bold text-blue-800 uppercase tracking-wider mb-3">
                          Video Content
                        </p>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Video URL
                          </label>
                          <input
                            type="url"
                            value={lessonForm.videoUrl}
                            onChange={(e) =>
                              setLessonForm({
                                ...lessonForm,
                                videoUrl: e.target.value,
                              })
                            }
                            placeholder="https://youtube.com/embed/..."
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                          />
                        </div>
                      </div>
                    )}

                    {/* Text Content */}
                    {(lessonForm.type === "text" ||
                      lessonForm.type === "mixed") && (
                      <div className="mb-5 p-4 bg-white rounded-lg border border-gray-200">
                        <p className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">
                          Text Content
                        </p>
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Content
                          </label>
                          <textarea
                            value={lessonForm.content}
                            onChange={(e) =>
                              setLessonForm({
                                ...lessonForm,
                                content: e.target.value,
                              })
                            }
                            placeholder="Lesson content..."
                            rows={5}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
                          />
                        </div>

                        {/* Topics Section */}
                        <div className="space-y-3 pt-3 border-t border-gray-200">
                          <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-gray-700">
                              Topics & Paragraphs
                            </label>
                            <button
                              type="button"
                              onClick={() => setShowAddTopic("add")}
                              className="text-sm text-primary hover:underline font-medium"
                            >
                              + Add Topic
                            </button>
                          </div>

                          {showAddTopic === "add" && (
                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                              <input
                                type="text"
                                value={topicForm.title}
                                onChange={(e) =>
                                  setTopicForm({
                                    ...topicForm,
                                    title: e.target.value,
                                  })
                                }
                                placeholder="Topic title"
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm"
                              />
                              <input
                                type="text"
                                value={topicForm.description}
                                onChange={(e) =>
                                  setTopicForm({
                                    ...topicForm,
                                    description: e.target.value,
                                  })
                                }
                                placeholder="Description (optional)"
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm"
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={handleAddTopic}
                                  className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90"
                                >
                                  Add
                                </button>
                                <button
                                  onClick={() => {
                                    setShowAddTopic(null);
                                    setTopicForm(defaultTopic);
                                  }}
                                  className="px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-100"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}

                          {lessonForm.topics.map((topic, tIndex) => (
                            <div
                              key={tIndex}
                              className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50"
                            >
                              {editingTopic === `topic-${tIndex}` ? (
                                <div className="space-y-2">
                                  <input
                                    type="text"
                                    value={topicForm.title}
                                    onChange={(e) =>
                                      setTopicForm({
                                        ...topicForm,
                                        title: e.target.value,
                                      })
                                    }
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                  />
                                  <input
                                    type="text"
                                    value={topicForm.description}
                                    onChange={(e) =>
                                      setTopicForm({
                                        ...topicForm,
                                        description: e.target.value,
                                      })
                                    }
                                    placeholder="Description"
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                  />
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => handleUpdateTopic(tIndex)}
                                      className="px-3 py-1.5 bg-primary text-white text-sm rounded-lg"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => {
                                        setEditingTopic(null);
                                        setTopicForm(defaultTopic);
                                      }}
                                      className="px-3 py-1.5 border border-gray-200 text-gray-600 text-sm rounded-lg"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="flex items-center justify-between mb-3">
                                    <div>
                                      <p className="text-base font-semibold text-gray-900">
                                        {topic.title}
                                      </p>
                                      {topic.description && (
                                        <p className="text-sm text-gray-500">
                                          {topic.description}
                                        </p>
                                      )}
                                    </div>
                                    <div className="flex gap-1">
                                      <button
                                        onClick={() =>
                                          startEditTopic(topic, tIndex)
                                        }
                                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg"
                                      >
                                        <Edit className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() =>
                                          handleDeleteTopic(tIndex)
                                        }
                                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                  <div className="space-y-3 pl-4 border-l-2 border-gray-300">
                                    <div className="flex items-center justify-between">
                                      <label className="text-sm font-medium text-gray-600">
                                        Paragraphs
                                      </label>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          addParagraphToTopic(tIndex)
                                        }
                                        className="text-sm text-gray-500 hover:text-primary font-medium"
                                      >
                                        + Add Paragraph
                                      </button>
                                    </div>
                                    {topic.paragraphs.map((para, pIndex) => (
                                      <div
                                        key={pIndex}
                                        className="flex items-start gap-2"
                                      >
                                        <textarea
                                          value={para.content}
                                          onChange={(e) =>
                                            updateParagraphContent(
                                              tIndex,
                                              pIndex,
                                              e.target.value,
                                            )
                                          }
                                          placeholder="Paragraph content..."
                                          rows={3}
                                          className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg text-sm resize-none"
                                        />
                                        <button
                                          type="button"
                                          onClick={() =>
                                            removeParagraphFromTopic(
                                              tIndex,
                                              pIndex,
                                            )
                                          }
                                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg shrink-0"
                                        >
                                          <X className="w-4 h-4" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Bullet Points */}
                    {(lessonForm.type === "video" ||
                      lessonForm.type === "mixed" ||
                      lessonForm.type === "text") && (
                      <div className="mb-5 p-4 bg-amber-100 rounded-lg border border-amber-200">
                        <label className="block text-sm font-bold text-amber-800 mb-2">
                          Bullet Points (one per line)
                        </label>
                        <textarea
                          value={lessonForm.bullets}
                          onChange={(e) =>
                            setLessonForm({
                              ...lessonForm,
                              bullets: e.target.value,
                            })
                          }
                          placeholder="Point 1&#10;Point 2&#10;Point 3"
                          rows={4}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                        />
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={() => handleAddLesson(module._id)}
                        disabled={saving}
                        className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
                      >
                        {saving ? (
                          <Loader className="w-4 h-4 animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4" />
                        )}
                        Create Lesson
                      </button>
                      <button
                        onClick={() => {
                          setShowAddLesson(null);
                          setLessonForm(defaultLessonForm);
                        }}
                        className="px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Edit Lesson Form */}
                {editingLesson && (
                  <div className="p-5 border-b border-gray-100 bg-green-50">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-base font-bold text-gray-900">
                        Edit Lesson
                      </h4>
                      <button
                        onClick={() => {
                          setEditingLesson(null);
                          setLessonForm(defaultLessonForm);
                        }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Basic Info */}
                    <div className="mb-5 p-4 bg-white rounded-lg border border-gray-200">
                      <p className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">
                        Basic Info
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Title *
                          </label>
                          <input
                            type="text"
                            value={lessonForm.title}
                            onChange={(e) =>
                              setLessonForm({
                                ...lessonForm,
                                title: e.target.value,
                              })
                            }
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Type
                          </label>
                          <select
                            value={lessonForm.type}
                            onChange={(e) =>
                              setLessonForm({
                                ...lessonForm,
                                type: e.target.value as any,
                              })
                            }
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                          >
                            <option value="video">Video</option>
                            <option value="text">Text</option>
                            <option value="code">Code</option>
                            <option value="mixed">Mixed</option>
                            <option value="quiz">Quiz</option>
                            <option value="assignment">Assignment</option>
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Duration (min)
                          </label>
                          <input
                            type="number"
                            value={lessonForm.duration}
                            onChange={(e) =>
                              setLessonForm({
                                ...lessonForm,
                                duration: parseInt(e.target.value) || 0,
                              })
                            }
                            min="0"
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                          />
                        </div>
                        <div className="col-span-2 flex items-center gap-6 pt-6">
                          <label className="flex items-center gap-2 text-sm text-gray-600">
                            <input
                              type="checkbox"
                              checked={lessonForm.isPreview}
                              onChange={(e) =>
                                setLessonForm({
                                  ...lessonForm,
                                  isPreview: e.target.checked,
                                })
                              }
                              className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                            />
                            Free Preview
                          </label>
                          <label className="flex items-center gap-2 text-sm text-gray-600">
                            <input
                              type="checkbox"
                              checked={lessonForm.isPublished}
                              onChange={(e) =>
                                setLessonForm({
                                  ...lessonForm,
                                  isPublished: e.target.checked,
                                })
                              }
                              className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                            />
                            Published
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Video Content */}
                    {(lessonForm.type === "video" ||
                      lessonForm.type === "mixed") && (
                      <div className="mb-5 p-4 bg-blue-100 rounded-lg border border-blue-200">
                        <p className="text-sm font-bold text-blue-800 uppercase tracking-wider mb-3">
                          Video Content
                        </p>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Video URL
                          </label>
                          <input
                            type="url"
                            value={lessonForm.videoUrl}
                            onChange={(e) =>
                              setLessonForm({
                                ...lessonForm,
                                videoUrl: e.target.value,
                              })
                            }
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                          />
                        </div>
                      </div>
                    )}

                    {/* Text Content */}
                    {(lessonForm.type === "text" ||
                      lessonForm.type === "mixed") && (
                      <div className="mb-5 p-4 bg-white rounded-lg border border-gray-200">
                        <p className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">
                          Text Content
                        </p>
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Content
                          </label>
                          <textarea
                            value={lessonForm.content}
                            onChange={(e) =>
                              setLessonForm({
                                ...lessonForm,
                                content: e.target.value,
                              })
                            }
                            rows={5}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
                          />
                        </div>

                        {/* Topics Section */}
                        <div className="space-y-3 pt-3 border-t border-gray-200">
                          <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-gray-700">
                              Topics & Paragraphs
                            </label>
                            <button
                              type="button"
                              onClick={() => setShowAddTopic("edit")}
                              className="text-sm text-primary hover:underline font-medium"
                            >
                              + Add Topic
                            </button>
                          </div>

                          {showAddTopic === "edit" && (
                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                              <input
                                type="text"
                                value={topicForm.title}
                                onChange={(e) =>
                                  setTopicForm({
                                    ...topicForm,
                                    title: e.target.value,
                                  })
                                }
                                placeholder="Topic title"
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm"
                              />
                              <input
                                type="text"
                                value={topicForm.description}
                                onChange={(e) =>
                                  setTopicForm({
                                    ...topicForm,
                                    description: e.target.value,
                                  })
                                }
                                placeholder="Description (optional)"
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm"
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={() => {
                                    if (!topicForm.title.trim()) {
                                      showError("Topic title is required");
                                      return;
                                    }
                                    setLessonForm({
                                      ...lessonForm,
                                      topics: [
                                        ...lessonForm.topics,
                                        { ...topicForm },
                                      ],
                                    });
                                    setTopicForm(defaultTopic);
                                    setShowAddTopic(null);
                                  }}
                                  className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90"
                                >
                                  Add
                                </button>
                                <button
                                  onClick={() => {
                                    setShowAddTopic(null);
                                    setTopicForm(defaultTopic);
                                  }}
                                  className="px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-100"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}

                          {lessonForm.topics.map((topic, tIndex) => (
                            <div
                              key={tIndex}
                              className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50"
                            >
                              {editingTopic === `topic-${tIndex}` ? (
                                <div className="space-y-2">
                                  <input
                                    type="text"
                                    value={topicForm.title}
                                    onChange={(e) =>
                                      setTopicForm({
                                        ...topicForm,
                                        title: e.target.value,
                                      })
                                    }
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                  />
                                  <input
                                    type="text"
                                    value={topicForm.description}
                                    onChange={(e) =>
                                      setTopicForm({
                                        ...topicForm,
                                        description: e.target.value,
                                      })
                                    }
                                    placeholder="Description"
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                  />
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => handleUpdateTopic(tIndex)}
                                      className="px-3 py-1.5 bg-primary text-white text-sm rounded-lg"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => {
                                        setEditingTopic(null);
                                        setTopicForm(defaultTopic);
                                      }}
                                      className="px-3 py-1.5 border border-gray-200 text-gray-600 text-sm rounded-lg"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="flex items-center justify-between mb-3">
                                    <div>
                                      <p className="text-base font-semibold text-gray-900">
                                        {topic.title}
                                      </p>
                                      {topic.description && (
                                        <p className="text-sm text-gray-500">
                                          {topic.description}
                                        </p>
                                      )}
                                    </div>
                                    <div className="flex gap-1">
                                      <button
                                        onClick={() =>
                                          startEditTopic(topic, tIndex)
                                        }
                                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg"
                                      >
                                        <Edit className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => {
                                          const updated = { ...lessonForm };
                                          updated.topics =
                                            updated.topics.filter(
                                              (_, i) => i !== tIndex,
                                            );
                                          setLessonForm(updated);
                                        }}
                                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                  <div className="space-y-3 pl-4 border-l-2 border-gray-300">
                                    <div className="flex items-center justify-between">
                                      <label className="text-sm font-medium text-gray-600">
                                        Paragraphs
                                      </label>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const updated = { ...lessonForm };
                                          updated.topics[
                                            tIndex
                                          ].paragraphs.push({ content: "" });
                                          setLessonForm(updated);
                                        }}
                                        className="text-sm text-gray-500 hover:text-primary font-medium"
                                      >
                                        + Add Paragraph
                                      </button>
                                    </div>
                                    {topic.paragraphs.map((para, pIndex) => (
                                      <div
                                        key={pIndex}
                                        className="flex items-start gap-2"
                                      >
                                        <textarea
                                          value={para.content}
                                          onChange={(e) => {
                                            const updated = { ...lessonForm };
                                            updated.topics[tIndex].paragraphs[
                                              pIndex
                                            ].content = e.target.value;
                                            setLessonForm(updated);
                                          }}
                                          placeholder="Paragraph content..."
                                          rows={3}
                                          className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg text-sm resize-none"
                                        />
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const updated = { ...lessonForm };
                                            updated.topics[tIndex].paragraphs =
                                              updated.topics[
                                                tIndex
                                              ].paragraphs.filter(
                                                (_, i) => i !== pIndex,
                                              );
                                            setLessonForm(updated);
                                          }}
                                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg shrink-0"
                                        >
                                          <X className="w-4 h-4" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Bullet Points */}
                    {(lessonForm.type === "video" ||
                      lessonForm.type === "mixed" ||
                      lessonForm.type === "text") && (
                      <div className="mb-5 p-4 bg-amber-100 rounded-lg border border-amber-200">
                        <label className="block text-sm font-bold text-amber-800 mb-2">
                          Bullet Points (one per line)
                        </label>
                        <textarea
                          value={lessonForm.bullets}
                          onChange={(e) =>
                            setLessonForm({
                              ...lessonForm,
                              bullets: e.target.value,
                            })
                          }
                          rows={4}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                        />
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={() => handleUpdateLesson(editingLesson)}
                        disabled={saving}
                        className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
                      >
                        {saving ? (
                          <Loader className="w-4 h-4 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                        Save Changes
                      </button>
                      <button
                        onClick={() => {
                          setEditingLesson(null);
                          setLessonForm(defaultLessonForm);
                        }}
                        className="px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Lessons List */}
                {module.lessons &&
                  module.lessons.length > 0 &&
                  !showAddLesson &&
                  !editingLesson && (
                    <div className="divide-y-2 divide-gray-100">
                      {module.lessons.map((lesson, lIndex) => (
                        <div
                          key={lesson._id}
                          className="flex items-center gap-4 p-4 hover:bg-blue-50"
                        >
                          <span className="w-8 text-base font-medium text-gray-400">
                            {lIndex + 1}
                          </span>
                          {getLessonIcon(lesson.type)}
                          <div className="flex-1">
                            <p className="text-base font-medium text-gray-900">
                              {lesson.title}
                            </p>
                            <p className="text-sm text-gray-500">
                              {lesson.type} · {formatDuration(lesson.duration)}
                              {lesson.isPreview && " · Free Preview"}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => startEditLesson(lesson)}
                              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteLesson(lesson._id)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                {/* Quiz Display */}
                {(module.hasQuiz || module.quiz) && !showAddQuiz && (
                  <div className="p-4 bg-amber-50 border-t border-amber-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <HelpCircle className="w-5 h-5 text-amber-600" />
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {module.quiz?.title || "Module Quiz"}
                          </p>
                          <p className="text-xs text-gray-500">
                            {module.quiz?.questions?.length || 0} questions ·{" "}
                            {module.quiz?.timeLimit || 30} min · Pass:{" "}
                            {module.quiz?.passingScore || 70}%
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          if (confirm("Delete this quiz?")) {
                            handleDeleteQuiz(module._id);
                          }
                        }}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Add Quiz Form */}
                {showAddQuiz === module._id && (
                  <div className="p-5 border-t border-gray-100 bg-amber-50">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-base font-bold text-gray-900">
                        Create Module Quiz
                      </h4>
                      <button
                        onClick={() => {
                          setShowAddQuiz(null);
                          setQuizForm({ title: "", description: "", timeLimit: 30, passingScore: 70, isCertificateExam: false, certificateTemplate: "default", examDuration: 120, minQuestions: 100 });
                        }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="space-y-4 bg-white p-4 rounded-lg border border-gray-200">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Quiz Title *
                        </label>
                        <input
                          type="text"
                          value={quizForm.title}
                          onChange={(e) => setQuizForm({ ...quizForm, title: e.target.value })}
                          placeholder="e.g., Module 1 Assessment"
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <textarea
                          value={quizForm.description}
                          onChange={(e) => setQuizForm({ ...quizForm, description: e.target.value })}
                          placeholder="Optional description for the quiz"
                          rows={2}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Time Limit (minutes)
                          </label>
                          <input
                            type="number"
                            value={quizForm.timeLimit}
                            onChange={(e) => setQuizForm({ ...quizForm, timeLimit: parseInt(e.target.value) || 30 })}
                            min={1}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Passing Score (%)
                          </label>
                          <input
                            type="number"
                            value={quizForm.passingScore}
                            onChange={(e) => setQuizForm({ ...quizForm, passingScore: parseInt(e.target.value) || 70 })}
                            min={1}
                            max={100}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                          />
                        </div>
                      </div>

                      {/* Certificate Exam Toggle */}
                      <div className="border-t pt-4 mt-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={quizForm.isCertificateExam}
                            onChange={(e) => setQuizForm({ ...quizForm, isCertificateExam: e.target.checked })}
                            className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary"
                          />
                          <span className="text-sm font-medium text-gray-700">
                            Certificate Exam (100 questions)
                          </span>
                        </label>
                        <p className="text-xs text-gray-500 ml-6 mt-1">
                          Generates certificate on pass. Requires 100 MCQs.
                        </p>
                        
                        {quizForm.isCertificateExam && (
                          <div className="grid grid-cols-2 gap-4 mt-3 ml-6">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Exam Duration (min)
                              </label>
                              <input
                                type="number"
                                value={quizForm.examDuration}
                                onChange={(e) => setQuizForm({ ...quizForm, examDuration: parseInt(e.target.value) || 120 })}
                                min={30}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Min Questions
                              </label>
                              <input
                                type="number"
                                value={quizForm.minQuestions}
                                onChange={(e) => setQuizForm({ ...quizForm, minQuestions: parseInt(e.target.value) || 100 })}
                                min={10}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      <p className="text-xs text-gray-500 mt-4">
                        Add questions now or create the quiz and add questions later.
                      </p>

                      {/* Questions Section */}
                      <div className="mt-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium text-gray-700">
                            Questions ({quizQuestions.length})
                          </label>
                          <button
                            type="button"
                            onClick={() => setShowAddQuestion(true)}
                            className="text-sm text-primary hover:underline font-medium"
                          >
                            + Add Question
                          </button>
                        </div>

                        {/* Add Question Form */}
                        {showAddQuestion && (
                          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Question Text *
                              </label>
                              <input
                                type="text"
                                value={newQuestion.questionText}
                                onChange={(e) => setNewQuestion({ ...newQuestion, questionText: e.target.value })}
                                placeholder="Enter your question"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Question Type
                              </label>
                              <select
                                value={newQuestion.type}
                                onChange={(e) => setNewQuestion({ 
                                  ...newQuestion, 
                                  type: e.target.value as any,
                                  options: e.target.value === "true-false" 
                                    ? [{ text: "True", isCorrect: false }, { text: "False", isCorrect: false }]
                                    : [{ text: "", isCorrect: false }, { text: "", isCorrect: false }]
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              >
                                <option value="multiple-choice">Multiple Choice</option>
                                <option value="true-false">True/False</option>
                                <option value="short-answer">Short Answer</option>
                              </select>
                            </div>

                            {newQuestion.type !== "short-answer" && (
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Options (select the correct answer)
                                </label>
                                <div className="space-y-2">
                                  {newQuestion.options.map((opt, idx) => (
                                    <div key={idx} className="flex items-center gap-2">
                                      <input
                                        type="radio"
                                        name={`correct-${showAddQuestion}`}
                                        checked={opt.isCorrect}
                                        onChange={() => handleUpdateOption(idx, "isCorrect", true)}
                                        className="w-4 h-4 text-primary"
                                      />
                                      <input
                                        type="text"
                                        value={opt.text}
                                        onChange={(e) => handleUpdateOption(idx, "text", e.target.value)}
                                        placeholder={`Option ${idx + 1}`}
                                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm"
                                      />
                                      {newQuestion.type === "multiple-choice" && newQuestion.options.length > 2 && (
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveOption(idx)}
                                          className="p-1 text-gray-400 hover:text-red-500"
                                        >
                                          <X className="w-4 h-4" />
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                {newQuestion.type === "multiple-choice" && (
                                  <button
                                    type="button"
                                    onClick={handleAddOption}
                                    className="mt-2 text-xs text-primary hover:underline"
                                  >
                                    + Add Option
                                  </button>
                                )}
                              </div>
                            )}

                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Points
                              </label>
                              <input
                                type="number"
                                value={newQuestion.points}
                                onChange={(e) => setNewQuestion({ ...newQuestion, points: parseInt(e.target.value) || 1 })}
                                min={1}
                                className="w-20 px-3 py-1.5 border border-gray-300 rounded text-sm"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Explanation (shown after answer)
                              </label>
                              <textarea
                                value={newQuestion.explanation}
                                onChange={(e) => setNewQuestion({ ...newQuestion, explanation: e.target.value })}
                                placeholder="Explain why the correct answer is correct..."
                                rows={2}
                                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm resize-none"
                              />
                            </div>

                            <div className="flex gap-2">
                              <button
                                onClick={handleAddQuestion}
                                className="px-3 py-1.5 bg-primary text-white text-xs rounded-lg"
                              >
                                Add Question
                              </button>
                              <button
                                onClick={() => {
                                  setShowAddQuestion(false);
                                  setNewQuestion({
                                    questionText: "",
                                    type: "multiple-choice",
                                    options: [{ text: "", isCorrect: false }, { text: "", isCorrect: false }],
                                    points: 1,
                                    explanation: "",
                                  });
                                }}
                                className="px-3 py-1.5 border border-gray-200 text-gray-600 text-xs rounded-lg"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Questions List */}
                        {quizQuestions.map((q, idx) => (
                          <div key={idx} className="p-3 bg-white rounded-lg border border-gray-200">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">{idx + 1}. {q.questionText}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {q.type} · {q.points} point{q.points > 1 ? "s" : ""}
                                  {q.options.length > 0 && ` · ${q.options.filter(o => o.isCorrect).length} correct`}
                                </p>
                              </div>
                              <button
                                onClick={() => handleRemoveQuestion(idx)}
                                className="p-1 text-gray-400 hover:text-red-500"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="flex gap-2 pt-4">
                        <button
                          onClick={() => handleCreateQuizWithQuestions(module._id)}
                          disabled={saving}
                          className="px-5 py-2.5 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
                        >
                          {saving ? <Loader className="w-4 h-4 animate-spin" /> : <HelpCircle className="w-4 h-4" />}
                          Create Quiz ({quizQuestions.length} questions)
                        </button>
                        <button
                          onClick={async () => {
                            if (!quizForm.title.trim()) {
                              showError("Quiz title is required");
                              return;
                            }
                            setSaving(true);
                            try {
                              await modulesApi.createQuiz(module._id, {
                                title: quizForm.title,
                                description: quizForm.description,
                                timeLimit: quizForm.timeLimit,
                                passingScore: quizForm.passingScore,
                              });
                              
                              const modulesRes = await modulesApi.getCourseModules(courseId!);
                              if (modulesRes.success) {
                                setModules(modulesRes.data);
                              }
                              setShowAddQuiz(null);
                              setQuizForm({ title: "", description: "", timeLimit: 30, passingScore: 70, isCertificateExam: false, certificateTemplate: "default", examDuration: 120, minQuestions: 100 });
                              setQuizQuestions([]);
                              showSuccess("Quiz created! You can add questions later.");
                            } catch (err: any) {
                              showError(err.message || "Failed to create quiz");
                            } finally {
                              setSaving(false);
                            }
                          }}
                          disabled={saving || !quizForm.title.trim()}
                          className="px-5 py-2.5 border border-gray-300 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        >
                          Save Quiz (Add Questions Later)
                        </button>
                        <button
                          onClick={() => {
                            setShowAddQuiz(null);
                            setQuizForm({ title: "", description: "", timeLimit: 30, passingScore: 70, isCertificateExam: false, certificateTemplate: "default", examDuration: 120, minQuestions: 100 });
                            setQuizQuestions([]);
                          }}
                          className="px-5 py-2.5 border border-gray-300 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {modules.length === 0 && !showAddModule && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-200">
          <BookOpen className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No modules yet</p>
          <p className="text-xs text-gray-400">
            Add your first module to start building the course
          </p>
        </div>
      )}
    </div>
  );
}
